import { type McpClientGrant, useStores } from '@littlebird/sdk';
import { format } from 'date-fns/format';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { handleExternalUrlClick } from '@/utils/platform';

import { mcpClientSearchId, settingsItemDomId } from '../settings-search-dom';
import { SettingsSection } from '../settings-section';

const MCP_SERVER_URL = 'https://mcp.littlebird.ai/mcp';
const MCP_SERVER_HELP_URL = 'https://support.littlebird.ai/docs/mcp/';
export const McpClientsSettings = observer(function McpClientsSettings() {
  const { mcpClientGrants } = useStores();

  if (!mcpClientGrants.isLoaded) {
    return (
      <YStack center sizeFull className="gap-2">
        <Icons.loader className="size-4 animate-spin text-foreground-muted" />
        <p className="text-foreground-muted text-xs">Loading MCP clients...</p>
      </YStack>
    );
  }

  const grants = Array.from(mcpClientGrants.data.values())
    .filter((grant) => !grant.deletedAt)
    .sort((a, b) => b.authorizedAt.getTime() - a.authorizedAt.getTime());

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>MCP Clients</SettingsSection.Title>
        <SettingsSection.Description>
          Manage clients authorized to access your Littlebird account.
        </SettingsSection.Description>
      </SettingsSection.Header>

      <XStack className="items-start gap-3 rounded-lg border border-danger-6 bg-danger-2 px-4 py-3">
        <Icons.warnTriangle className="mt-0.5 size-4 shrink-0 text-danger-11" />
        <p className="text-sm leading-5 text-danger-11">
          MCP clients can use Littlebird tools to search your captured context,
          which may contain private data. Only approve a connection you started
          with a client you recognize.
        </p>
      </XStack>

      <SettingsSection.Group
        title="Connect a client"
        trailing={
          <Button
            variant="link"
            size="sm"
            onClick={() => handleExternalUrlClick(MCP_SERVER_HELP_URL)}
          >
            How to connect
            <Icons.externalLink2 className="size-3" />
          </Button>
        }
      >
        <SettingsSection.Item>
          <YStack className="min-w-0">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                MCP server URL
              </SettingsSection.ItemLabel>
              <code className="break-all font-mono text-sm text-foreground-muted">
                {MCP_SERVER_URL}
              </code>
            </SettingsSection.ItemHeader>
          </YStack>
          <CopyButton
            text={MCP_SERVER_URL}
            copyText="Copy MCP server URL"
            size="sm"
            aria-label="Copy MCP server URL"
          />
        </SettingsSection.Item>
      </SettingsSection.Group>

      <SettingsSection.Group title="Connected clients">
        {grants.length > 0 ? (
          <>
            {grants.map((grant, index) => (
              <McpClientRow
                key={grant.id}
                grant={grant}
                separated={index > 0}
              />
            ))}
            <SettingsSection.Seperator />
            <p className="text-xs text-foreground-muted">
              Revoking access takes effect immediately. The client must
              authorize again to reconnect.
            </p>
          </>
        ) : (
          <p className="text-sm text-foreground-muted">
            No MCP clients authorized yet. Clients you connect will appear here.
          </p>
        )}
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
});

// ---------------------------------------------------------------------------
// MCP client row
// ---------------------------------------------------------------------------

function McpClientRow({
  grant,
  separated,
}: {
  grant: McpClientGrant;
  separated: boolean;
}) {
  const { mcpClientGrants } = useStores();
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const handleRevoke = async () => {
    setShowRevokeConfirm(false);
    setIsRevoking(true);
    try {
      await mcpClientGrants.revokeClientGrant(grant);
    } catch {
      toast.error('Failed to revoke MCP client access');
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <>
      {separated ? <SettingsSection.Seperator /> : null}
      <SettingsSection.Item
        // anchors the row for search deep links
        id={settingsItemDomId(mcpClientSearchId(grant.id))}
        className="w-full flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center"
      >
        <YStack className="min-w-0 break-words">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              {grant.clientName}
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Authorized {format(grant.authorizedAt, 'MMM d, yyyy')}
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
        </YStack>
        <Button
          variant="destructive-ghost"
          size="sm"
          onClick={() => setShowRevokeConfirm(true)}
          disabled={isRevoking}
          isLoading={isRevoking}
          aria-label={`Revoke access for ${grant.clientName}`}
          className="self-start sm:self-auto"
        >
          Revoke
        </Button>
      </SettingsSection.Item>

      <Dialog.Root open={showRevokeConfirm} onOpenChange={setShowRevokeConfirm}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>Revoke {grant.clientName} access</Dialog.Title>
            <Dialog.Description>
              This will revoke {grant.clientName}'s access to your Littlebird
              account. The MCP client will need to re-authorize to regain
              access.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowRevokeConfirm(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleRevoke}>
              Revoke
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
